import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";
import { selectExtendedVariant, extendedMaySource } from "../../../extendedVariantSelection";


const TYPE = "MaturaExponentialModelOpenTask";

const MID_FACTORS = [2, 3, 4, 5];
const GROWTH_CONFIGS = [
  [11, 10], [6, 5], [5, 4], [7, 5], [3, 2], [7, 4],
].flatMap(([num, den]) => [2, 3].flatMap((t) =>
  [25, 50, 100, 125, 250, 500, 625, 1000].map((multiple) => ({
    n0: den ** t * multiple, t, later: num ** t * multiple,
    factor: num / den, result: Math.round((num / den - 1) * 100),
  }))
)).filter((c) => c.n0 <= 100000 && c.later <= 500000
  && !(c.n0 === 10000 && c.t === 2 && c.factor === 1.25));
export function getVariantCount() { return GROWTH_CONFIGS.length; }

function rand(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function rawValue(value) {
  if (value == null) return "";
  if (typeof value === "object" && value.target && "value" in value.target) {
    return String(value.target.value ?? "");
  }
  if (typeof value === "object") {
    for (const key of ["raw", "text", "value", "latex", "expr"]) {
      if (value[key] != null && typeof value[key] !== "object") {
        return String(value[key]);
      }
    }
  }
  return String(value);
}

function normalize(raw) {
  return rawValue(raw)
    .toLowerCase()
    .replace(/\\left|\\right/g, "")
    .replace(/\\cdot|\\times|·|×/g, "*")
    .replace(/\{,\}/g, ".")
    .replace(/,/g, ".")
    .replace(/\s+/g, "");
}

function parseNumber(raw) {
  const source = rawValue(raw).trim();
  const hasPercent = /%/.test(source);
  const s = normalize(source).replace(/[^0-9.\-+/]/g, "");
  if (!s) return null;
  let value = null;
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    value = Number(s);
  } else if (/^-?\d+(\.\d+)?\/-?\d+(\.\d+)?$/.test(s)) {
    const [a, b] = s.split("/").map(Number);
    value = b === 0 ? null : a / b;
  }
  if (value == null || !Number.isFinite(value)) return null;
  return hasPercent ? value / 100 : value;
}

function isClose(raw, expected, tolerance = 1e-9) {
  const value = parseNumber(raw);
  return value != null && Math.abs(value - expected) <= tolerance;
}

function toLatexExpression(raw) {
  const s = rawValue(raw).trim();
  if (!s) return "?";
  return s
    .replace(/,/g, "{,}")
    .replace(/\*/g, "\\cdot ")
    .replace(/\^\(([^)]+)\)/g, "^{$1}")
    .replace(/\^(\d+)/g, "^{$1}");
}

function decimalLatex(value) {
  return String(value).replace(".", ",").replace(",", "{,}");
}

function formatNumber(value) {
  return String(Number(value.toFixed(4))).replace(".", ",");
}

function setField(answer, key, value, onChange) {
  onChange && onChange({ ...(answer || {}), [key]: value });
}

function generateGrowthScenario(kind, options = {}) {
  return { type: TYPE, points: 2, variant: kind, mode: "growth-percent",
    ...selectExtendedVariant(GROWTH_CONFIGS, options) };
}

function generateMiddleScenario(kind) {
  const stepFactor = rand(MID_FACTORS);
  const firstValue = rand([2, 3, 4, 5, 6, 8]);
  const t1 = rand([2, 3, 4]);
  const t2 = t1 + 2;
  const middleT = t1 + 1;
  const secondValue = firstValue * stepFactor * stepFactor;
  const middleValue = firstValue * stepFactor;
  const beta = 1 / stepFactor;

  return {
    type: TYPE,
    points: 2,
    variant: kind,
    mode: "middle-value",
    t1,
    t2,
    middleT,
    firstValue,
    secondValue,
    factor: beta,
    result: middleValue,
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2025_MAJ") {
    return {
      type: TYPE, points: 2, variant: "bacteria", mode: "growth-percent",
      n0: 10000, t: 2, later: 15625, factor: 1.25, result: 25,
      source: { variant: "original", exam: "MATURA", session: "2025_MAJ", taskNumber: "1", basedOn: "MMAP-R0-100-A-2505-1" },
    };
  }
  if (options.session === "2025_MAJ" || options.variantIndex != null || options.seed != null) {
    return { ...generateGrowthScenario("bacteria", options), source: extendedMaySource("1") };
  }
  const variants = [
    () => generateGrowthScenario("bacteria"),
    () => generateGrowthScenario("yeast"),
    () => generateMiddleScenario("capacitor"),
    () => generateMiddleScenario("medicine"),
  ];
  const task = rand(variants)();
  task.source = task.mode === "growth-percent"
    ? { variant: "random", basedOn: "MMAP-R0-100-A-2505-1" }
    : { variant: "random" };
  return task;
}

function renderGrowthTask(task) {
  const noun = task.variant === "yeast" ? "drożdży" : "bakterii";
  const species =
    task.variant === "yeast"
      ? "W warunkach laboratoryjnych obserwowano dynamikę wzrostu masy kolonii drożdży."
      : "W warunkach laboratoryjnych obserwowano dynamikę wzrostu liczebności populacji pewnego gatunku bakterii.";

  return (
    <>
      <p>{species}</p>
      <p>
        Wielkość <InlineMath math="N" /> populacji zmienia się w czasie zgodnie
        z zależnością wykładniczą
      </p>
      <BlockMath math="N(t)=N_0\cdot k^t,\qquad t\ge 0" />
      <p>
        gdzie <InlineMath math="N_0" /> oznacza wielkość populacji w chwili{" "}
        <InlineMath math="t=0" />, <InlineMath math="k" /> jest dodatnią stałą,
        a <InlineMath math="t" /> oznacza czas w godzinach.
      </p>
      <p>
        W chwili rozpoczęcia obserwacji wielkość populacji {noun} była równa{" "}
        <InlineMath math={String(task.n0)} />, a po <InlineMath math={String(task.t)} /> godzinach była
        równa <InlineMath math={String(task.later)} />.
      </p>
      <p>
        <strong>
          Oblicz, o ile procent wzrastała wielkość tej populacji w ciągu każdej
          godziny. Zapisz obliczenia.
        </strong>
      </p>
    </>
  );
}

function renderMiddleTask(task) {
  if (task.variant === "medicine") {
    return (
      <>
        <p>
          Ilość pewnej substancji w roztworze opisano zależnością wykładniczą
        </p>
        <BlockMath math="M(t)=M_0\cdot \beta^{-t},\qquad t\ge 0" />
        <p>
          gdzie <InlineMath math="M(t)" /> oznacza ilość substancji po czasie{" "}
          <InlineMath math="t" /> wyrażonym w godzinach, a{" "}
          <InlineMath math="\beta" /> jest dodatnią stałą.
        </p>
        <p>
          Wiadomo, że w chwili <InlineMath math={`t=${task.t1}`} /> ilość
          substancji była równa <InlineMath math={String(task.firstValue)} />,
          a w chwili <InlineMath math={`t=${task.t2}`} /> była równa{" "}
          <InlineMath math={String(task.secondValue)} />.
        </p>
        <p>
          <strong>
            Oblicz ilość tej substancji w chwili{" "}
            <InlineMath math={`t=${task.middleT}`} />. Zapisz obliczenia.
          </strong>
        </p>
      </>
    );
  }

  return (
    <>
      <p>
        Ładunek elektryczny zgromadzony w kondensatorze można opisać zależnością
      </p>
      <BlockMath math="Q(t)=Q_0\cdot \beta^{-t},\qquad t\ge 0" />
      <p>
        gdzie <InlineMath math="Q(t)" /> oznacza ładunek elektryczny w chwili{" "}
        <InlineMath math="t" /> wyrażonej w sekundach, a{" "}
        <InlineMath math="\beta" /> jest dodatnią stałą.
      </p>
      <p>
        Wiadomo, że w chwili <InlineMath math={`t=${task.t1}\\,\\text{s}`} /> w
        kondensatorze był zgromadzony ładunek{" "}
        <InlineMath math={String(task.firstValue)} /> mikrokulombów, a w chwili{" "}
        <InlineMath math={`t=${task.t2}\\,\\text{s}`} /> - ładunek{" "}
        <InlineMath math={String(task.secondValue)} /> mikrokulombów.
      </p>
      <p>
        <strong>
          Oblicz, ile mikrokulombów ładunku było zgromadzone w tym
          kondensatorze w chwili{" "}
          <InlineMath math={`t=${task.middleT}\\,\\text{s}`} />. Zapisz
          obliczenia.
        </strong>
      </p>
    </>
  );
}

export function renderTask(task) {
  return (
    <div className="space-y-4">
      <p>
        <strong>Zadanie maturalne - otwarte (0-2 pkt).</strong>
      </p>
      {task.mode === "growth-percent" ? renderGrowthTask(task) : renderMiddleTask(task)}
    </div>
  );
}

export function renderInput(task, userAnswer = {}, onChange, disabled) {
  const symbol = task.mode === "growth-percent" ? "k" : "\\beta";
  const resultLabel =
    task.mode === "growth-percent"
      ? "Procentowy wzrost w ciągu godziny"
      : "Szukana wartość";
  const resultPlaceholder = task.mode === "growth-percent" ? "np. 25%" : "np. 6";
  const resultMathLabel =
    task.mode === "growth-percent"
      ? "p"
      : `${task.variant === "capacitor" ? "Q" : "M"}(${task.middleT})`;

  return (
    <div className="space-y-5">
      <OpenTaskScoringHint variant="independent" />
      <div>
        <p>
          <strong>Krok 1 (1 pkt).</strong> Wyznacz stałą modelu{" "}
          <InlineMath math={symbol} />.
        </p>
        <div style={{ marginBottom: 6 }}>
          <InlineMath math={`${symbol} =`} />
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <EquationInput
            value={userAnswer.factor || ""}
            onChange={(v) => setField(userAnswer, "factor", v, onChange)}
            disabled={disabled}
            placeholder={task.mode === "growth-percent" ? "np. 1,25" : "np. 1/3"}
            showPreview={true}
            label={symbol}
            basis="220px"
          />
        </div>
      </div>

      <div>
        <p>
          <strong>Krok 2 (1 pkt).</strong> Podaj wynik końcowy.
        </p>
        <div style={{ marginBottom: 6 }}>
          <span>{resultLabel}: </span>
          <InlineMath math={`${resultMathLabel} =`} />
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <EquationInput
            value={userAnswer.result || ""}
            onChange={(v) => setField(userAnswer, "result", v, onChange)}
            disabled={disabled}
            placeholder={resultPlaceholder}
            showPreview={true}
            label={resultMathLabel}
            basis="220px"
          />
        </div>
      </div>
    </div>
  );
}

export function checkAnswer(task, userAnswer = {}) {
  const factorOk = isClose(userAnswer.factor, task.factor, 1e-9);
  let resultOk = false;

  if (task.mode === "growth-percent") {
    resultOk =
      isClose(userAnswer.result, task.result, 1e-9) ||
      isClose(userAnswer.result, task.result / 100, 1e-9);
  } else {
    resultOk = isClose(userAnswer.result, task.result, 1e-9);
  }

  const parts = [
    { key: "factor", label: "stała modelu", correct: factorOk },
    { key: "result", label: "wynik końcowy", correct: resultOk },
  ];
  const score = parts.filter((part) => part.correct).length;

  return {
    isCorrect: score === 2,
    correct: score === 2,
    score,
    maxScore: 2,
    parts,
  };
}

export function renderUserAnswer(userAnswer = {}, task) {
  const symbol = task?.mode === "growth-percent" ? "k" : "\\beta";
  const result = task?.mode === "growth-percent" ? "p" : "W";
  return (
    <div className="space-y-1">
      <p>
        <InlineMath math={`${symbol}=${toLatexExpression(userAnswer.factor)}`} />
      </p>
      <p>
        <InlineMath math={`${result}=${toLatexExpression(userAnswer.result)}`} />
      </p>
    </div>
  );
}

export function renderAnswer(task) {
  if (task.mode === "growth-percent") {
    return (
      <div className="space-y-2">
        <p>
          <strong>Krok 1:</strong>{" "}
          <InlineMath
            math={`${task.later}=${task.n0}\\cdot k^{${task.t}},\\quad k=${decimalLatex(
              formatNumber(task.factor)
            )}`}
          />
        </p>
        <p>
          <strong>Krok 2:</strong>{" "}
          <InlineMath math={`(${decimalLatex(formatNumber(task.factor))}-1)\\cdot 100\\%=${task.result}\\%`} />
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong>{" "}
        <InlineMath
          math={`\\frac{${task.secondValue}}{${task.firstValue}}=\\beta^{-2},\\quad \\beta=${decimalLatex(
            formatNumber(task.factor)
          )}`}
        />
      </p>
      <p>
        <strong>Krok 2:</strong>{" "}
        <InlineMath math={`W(${task.middleT})=${task.result}`} />
      </p>
    </div>
  );
}

export function renderExplanation(task) {
  if (task.mode === "growth-percent") {
    return (
      <div className="space-y-4">
        <p>
          <strong>Krok 1.</strong> Podstawiamy dane do zależności wykładniczej.
        </p>
        <BlockMath math={`${task.later}=${task.n0}\\cdot k^{${task.t}}`} />
        <BlockMath
          math={`k^{${task.t}}=\\frac{${task.later}}{${task.n0}}=${decimalLatex(
            formatNumber(Math.pow(task.factor, task.t))
          )}`}
        />
        <BlockMath math={`k=${decimalLatex(formatNumber(task.factor))}`} />

        <p>
          <strong>Krok 2.</strong> Stała <InlineMath math="k" /> oznacza, że po
          każdej godzinie wielkość populacji jest mnożona przez{" "}
          <InlineMath math={decimalLatex(formatNumber(task.factor))} />.
        </p>
        <BlockMath
          math={`(${decimalLatex(formatNumber(task.factor))}-1)\\cdot 100\\%=${task.result}\\%`}
        />
      </div>
    );
  }

  const symbol = task.variant === "capacitor" ? "Q" : "M";
  const unit = task.variant === "capacitor" ? "\\,\\mu C" : "";
  return (
    <div className="space-y-4">
      <p>
        <strong>Krok 1.</strong> Dzielimy równania dla dwóch podanych chwil, żeby
        wyznaczyć stałą <InlineMath math={"\\beta"} />.
      </p>
      <BlockMath
        math={`\\frac{${task.secondValue}}{${task.firstValue}}=\\frac{${symbol}_0\\beta^{-${task.t2}}}{${symbol}_0\\beta^{-${task.t1}}}=\\beta^{-2}`}
      />
      <BlockMath
        math={`\\beta^{-2}=${Math.pow(1 / task.factor, 2)},\\qquad \\beta=${decimalLatex(
          formatNumber(task.factor)
        )}`}
      />

      <p>
        <strong>Krok 2.</strong> Wartość w chwili pośredniej jest iloczynem
        wcześniejszej wartości i jednego czynnika wzrostu.
      </p>
      <BlockMath
        math={`${symbol}(${task.middleT})=${task.firstValue}\\cdot ${1 / task.factor}=${task.result}${unit}`}
      />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};
